import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, IconButton, TextField, Typography } from "@mui/material";
import MaterialReactTable from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { UrlApi } from "../../../services/apirest";
import { formatoPesoArgentino } from "../../../services/library";

// Historial de cambios de precios especiales de las sucursales autorizadas
function HistorialPreciosEspeciales({ onClose }) {
  const [historial, setHistorial] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState(false);
  const [desde, setDesde] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [hasta, setHasta] = useState(moment().format("YYYY-MM-DD"));

  const obtenerHistorial = async () => {
    const sucAuth = localStorage.getItem("suc_auth")?.split(",") || [];
    if (sucAuth.length === 0) {
      setHistorial([]);
      setCargando(false);
      return;
    }
    setCargando(true);
    setErrorCarga(false);
    try {
      const params = new URLSearchParams();
      if (desde) params.append("desde", desde);
      if (hasta) params.append("hasta", hasta);
      const res = await fetch(
        `${UrlApi}/envasado_historial_especial/historialsucauth/${sucAuth}?${params}`
      );
      if (!res.ok) throw new Error("Error al obtener el historial");
      const data = await res.json();
      setHistorial(data ?? []);
    } catch (err) {
      console.error(err);
      setErrorCarga(true);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    obtenerHistorial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo(
    () => [
      {
        header: "Fecha",
        accessorKey: "fecha",
        size: 140,
        enableGrouping: false,
        sortingFn: (a, b) =>
          (a.original.fecha_orden ?? "").localeCompare(b.original.fecha_orden ?? ""),
      },
      { header: "Usuario", accessorKey: "usuario", size: 120 },
      { header: "Cliente", accessorKey: "nombre_cliente", size: 220 },
      { header: "Producto", accessorKey: "nombre_producto", size: 180 },
      { header: "Sucursal", accessorKey: "nombre_sucursal", size: 120 },
      {
        header: "Anterior",
        accessorKey: "precio_minimo_anterior",
        size: 110,
        enableGrouping: false,
        Cell: ({ cell }) =>
          cell.getValue() === null || cell.getValue() === undefined
            ? "-"
            : formatoPesoArgentino(cell.getValue()),
      },
      {
        header: "Nuevo",
        accessorKey: "precio_minimo_nuevo",
        size: 110,
        enableGrouping: false,
        Cell: ({ cell }) =>
          cell.getValue() === null || cell.getValue() === undefined
            ? "-"
            : formatoPesoArgentino(cell.getValue()),
      },
    ],
    []
  );

  const tableHeadCellProps = {
    align: "left",
    sx: {
      fontSize: 16,
      color: "white",
      fontWeight: "normal",
      backgroundColor: "#2471A3",
      borderColor: "black",
    },
  };

  const muiSearchTextFieldProps = {
    placeholder: "Buscar..",
    sx: { minWidth: "18rem", backgroundColor: "#ffffff" },
    variant: "outlined",
    color: "success",
    size: "small",
  };

  const fechasInvalidas = desde && hasta && desde > hasta;

  return (
    <>
      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={2}>
        <TextField
          size="small"
          type="date"
          label="Desde"
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          type="date"
          label="Hasta"
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="contained"
          onClick={obtenerHistorial}
          disabled={cargando || fechasInvalidas}
        >
          Buscar
        </Button>
        {fechasInvalidas && (
          <Typography variant="body2" color="error">
            La fecha "desde" no puede ser mayor que "hasta".
          </Typography>
        )}
      </Box>

      {errorCarga ? (
        <Alert severity="error">No se pudo cargar el historial.</Alert>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={historial}
          state={{ isLoading: cargando }}
          layoutMode={"grid"}
          initialState={{
            density: "compact",
            pagination: { pageIndex: 0, pageSize: 25 },
          }}
          enableColumnActions={false}
          enableGlobalFilterModes={false}
          enableHiding={false}
          enableGrouping={true}
          enableColumnFilters={false}
          enableFullScreenToggle={false}
          enableDensityToggle={false}
          enableColumnResizing={true}
          localization={MRT_Localization_ES}
          muiTableHeadCellProps={tableHeadCellProps}
          muiSearchTextFieldProps={muiSearchTextFieldProps}
          positionToolbarAlertBanner="none"
          muiTableBodyCellProps={{ align: "left" }}
          renderTopToolbarCustomActions={() => (
            <Box display="flex" alignItems="center">
              <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
                <CloseIcon />
              </IconButton>
              <Typography variant="poster" fontSize={26}>
                Historial
              </Typography>
            </Box>
          )}
        />
      )}
    </>
  );
}

export default HistorialPreciosEspeciales;
